import { Button, Divider, Label, makeStyles, shorthands, Text, tokens, Tooltip } from '@fluentui/react-components';
import { ChatAdd24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations, useGptConfigurations, useUserPreferences, useFeature, useRootState } from '../../store/hooks';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import { FeatureHighlightPopup } from '../Common/FeatureHighlightPopup';
import AppMenuButton from './AppMenuButton';
import { Clover24Regular } from '@fluentui/react-icons';
import { ExploreGptRoute } from '../../Routes';
import { getGptIconAsset } from '../../Utils/image';
import SyButton from '../Common/SyButton';
import { Globe, LayoutGrid, MessagesSquare } from 'lucide-react';
import SyChevron from './SyChevron';
import { EllipsisText } from '../Common/EllipsisText';
import { DarkModeIcon } from '../Common/DarkModeIcon';

interface MenuCommandsProps {
    onNewDraftChat: (gptConfiguration: IGptConfiguration) => void;
    showOnlyToggleMenuCommand?: boolean;
    onCloseMenuRequired?: () => void;
}

const useStyles = makeStyles({
    commands: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('0px')
    },

    exploreGPT: {
        marginLeft: '3px',
    },

    rowButton: {
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('6px'),
        padding: '0px 8px 0px 2px',  // top, right, bottom, left
        width: 'calc(100% - 23px)',
        minHeight: '38px',
        marginLeft: '7px',
        marginRight: '16px',
        cursor: 'pointer',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        transition: 'background-color 120ms ease',
        '&:hover': {
            backgroundColor: 'var(--menu-button-hover-bg) !important'
        },
        '&:focus': {
            outline: 'none',
            backgroundColor: 'var(--menu-button-hover-bg) !important'
        },
        '&:active': {
            backgroundColor: 'var(--color-neutral-200) !important'
        },
        '&:focus-visible': {
            boxShadow: '0 0 0 2px rgba(0,120,212,0.25)'
        }
    },
    cardHeader: {
        gridTemplateRows: '100%',
        paddingLeft: '0px',
        marginLeft: '4px',
        minWidth: '0',
        flex: '1'
    },
    actionContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
    },
    gptIconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        minWidth: '20px',
        marginLeft: '8px',
    }
    ,
    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'left',
        borderRadius: '7px',
        padding: '4px'
    },
    iconImage: {
        width: '16px',
        height: '16px',
        objectFit: 'contain',
        borderRadius: '2px',
        backgroundColor: 'transparent'
    },
    gptName: {
    },
    gptNameWrapper: {
        minWidth: '0',
        paddingLeft: '6px',
        paddingRight: '4px',
    },
    gptNameContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    }

});

const MenuCommands: FC<MenuCommandsProps> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { onNewDraftChat, showOnlyToggleMenuCommand, onCloseMenuRequired } = props;
    const [hoveredGptId, setHoveredGptId] = useState<string | null>(null);
    const { chats } = useConversations();
    const [isNewChatEnabled, setIsNewChatEnabled] = useState(false);
    const { gptConfigurations } = useGptConfigurations();
    const chatFeature = useFeature('chat');
    const isPersonalGptEnabled = chatFeature.personalGpt?.isEnabled ?? false;
    const showExploreGpt = (gptConfigurations?.some(gpt => !gpt.isDefault && !gpt.isPersonal) || isPersonalGptEnabled) ?? false;
    const navigate = useNavigate();
    const { userPreferences, dispatch } = useUserPreferences();
    const [addedGpts, setAddedGpts] = useState<IGptConfiguration[]>([]);
    const [gptsExpanded, setGptsExpanded] = useState<boolean>(true);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const { state } = useRootState();
    const appLoading = state.appLoading ?? false;
    const conversationsLoading = state.conversationsLoading ?? false;
    const loading = appLoading || conversationsLoading;

    useEffect(() => {
        const updateDarkMode = () => {
            setIsDarkMode(document.body.getAttribute('data-theme') === 'dark');
        };

        updateDarkMode();

        const observer = new MutationObserver(updateDarkMode);
        // Look for the last conversation based on latest lastUpdateOn (or startedOn as fallback)
        // Create a copy before sorting to avoid mutating Redux state
        const lastConversation = [...chats].sort((a, b) => {
            const aDate = a.lastUpdateOn ?? a.startedOn;
            const bDate = b.lastUpdateOn ?? b.startedOn;
            return aDate > bDate ? -1 : 1;
        })[0];
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });

        return () => observer.disconnect();
    }, []);

    const handleToggleGpts = useCallback((e?: React.KeyboardEvent | React.MouseEvent) => {
        if (e && 'key' in (e as React.KeyboardEvent)) {
            const key = (e as React.KeyboardEvent).key;
            if (key !== 'Enter' && key !== ' ') return;
            e.preventDefault();
        }
        setGptsExpanded((s) => !s);
    }, []);

    useEffect(() => {
        // Look for the last conversation based on latest startedOn property, excluding draft conversations
        const nonDraftChats = chats.filter(c => !c.draft);
        const lastConversation = nonDraftChats.sort((a, b) => (a.startedOn > b.startedOn ? -1 : 1))[0];
        const isConversationEmpty = !lastConversation?.prompts || lastConversation.prompts.length === 0;
        setIsNewChatEnabled(!isConversationEmpty);
    }, [chats]);

    useEffect(() => {
        setAddedGpts(gptConfigurations.filter(x => userPreferences.addedGpts?.includes(x.id!) || x.isDefault).sort((a, b) => Number(b.isDefault) - Number(a.isDefault)))
    }, [userPreferences, gptConfigurations]);

    // If showing only toggle command (collapsed mode), don't render anything
    // since toggle is now handled by PageLayout
    if (showOnlyToggleMenuCommand) {
        return null;
    }

    const gpt = addedGpts.find(g => g.isDefault) || addedGpts[0];

    return (
        <div style={{ paddingTop: 6 }}>

            {showExploreGpt && (
                <div className="sectionLabelWithChevron" >
                    <SyChevron
                        expanded={gptsExpanded}
                        onToggle={handleToggleGpts}
                        ariaControls="menu-gpts-list"
                        title={t('menu.gpts.toggle', 'Toggle GPTs')}
                        iconClassName={'sy-chevron-base'}
                    />

                    <Label className="menuSectionLabel" size='small' style={{ marginLeft: 3 }}>
                        {t('menu.gpts.title', 'GPTs')}
                    </Label>
                </div>
            )}

            {/* Explore GPTs button inserted here */}
            {gptsExpanded && showExploreGpt && (
                <FeatureHighlightPopup
                    id={'syGPTTool3C'}
                    header={t('featureHighlights.header')}
                    title={t('featureHighlights.exploreGPT.title')}
                    content={t('featureHighlights.exploreGPT.content')}
                    link={t('featureHighlights.exploreGPT.link')}
                    position={'after'}
                >
                    <div className={classes.exploreGPT}>
                        <AppMenuButton
                            title={'menu.gpts.explore'}
                            label={'menu.gpts.explore'}
                            color={'var(--color-primary-500)'}
                            icon={<LayoutGrid />}
                            changeBackgroundOnHover={false}
                            onClick={() => {
                                navigate(ExploreGptRoute);
                                // Close menu in mobile mode when navigating to explore GPTs
                                if (onCloseMenuRequired) {
                                    onCloseMenuRequired();
                                }
                            }}
                        />
                    </div>
                </FeatureHighlightPopup>
            )}

            {gptsExpanded && (
                <div className={classes.commands}>
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6 }}>
                            {(() => {
                                return (
                                    <>
                                        <Skeleton className="w-80p" />
                                        <Skeleton className="w-70p" />
                                        <Skeleton className="w-60p" />
                                    </>
                                );
                            })()}
                        </div>
                    ) : (
                        <>
                            {addedGpts.filter(g => !g.isDefault).map((gpt) => {
                                const activate = () => {
                                    onNewDraftChat(gpt);
                                    if (onCloseMenuRequired) {
                                        onCloseMenuRequired();
                                    }
                                };

                                return (
                                    <div
                                        key={gpt.id}
                                        className={classes.rowButton}
                                        role="button"
                                        tabIndex={0}
                                        onClick={activate}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                activate();
                                            }
                                        }}
                                        onMouseEnter={() => setHoveredGptId(gpt.id ?? null)}
                                        onMouseLeave={() => setHoveredGptId(null)}
                                        onFocus={() => setHoveredGptId(gpt.id ?? null)}
                                        onBlur={() => setHoveredGptId(null)}
                                        style={{
                                            backgroundColor: hoveredGptId === gpt.id ? 'var(--menu-button-hover-bg)' : 'transparent',
                                            transition: 'background-color 120ms ease'
                                        }}
                                    >

                                        <div className={classes.cardHeader}>
                                            <div className={classes.gptNameContainer}>
                                                <div className={classes.gptNameWrapper} style={{ color: isDarkMode ? 'var(--color-zinc-300)' : 'inherit' }}>
                                                    <EllipsisText hover={hoveredGptId === gpt.id}>{gpt.name}</EllipsisText>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            )}

            {showExploreGpt && (
                <Divider className="menuSmallDividerIn" />
            )}

        </div>
    );
};

export default MenuCommands;
